import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { api } from '@/modules/requests/api';
import { RequestDetail } from '@/modules/requests/ui/RequestDetail';
import { Header } from '@/app/components/Header';
import { Footer } from '@/app/components/Footer';
import { AuthModal } from '@/app/components/AuthModal';
import { listState } from '@/modules/core/listState';
import * as Sentry from '@sentry/browser';

export function RequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    const fetchRequest = async () => {
      if (!id) {
        console.error('RequestDetailPage: No request ID provided');
        setError('No request ID provided');
        setLoading(false);
        return;
      }

      try {
        console.log('RequestDetailPage: Fetching request details for ID:', id);
        setLoading(true);
        setError(null);

        // For now, we'll fetch all requests and find the one we need
        // In a production app, you'd want a dedicated endpoint for single request fetching
        let requests;
        if (user) {
          requests = await api.getRequests();
        } else {
          requests = await api.getPublicRequests();
        }

        const foundRequest = requests.find(r => r.id === id);
        
        if (!foundRequest) {
          console.error('RequestDetailPage: Request not found:', id);
          setError('Request not found');
          setLoading(false);
          return;
        }

        setRequest(foundRequest);
        
        // Update page title and meta description
        document.title = `${foundRequest.title} - Zaptopia`;
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          const truncatedDescription = foundRequest.description.length > 150 
            ? foundRequest.description.substring(0, 150) + '...'
            : foundRequest.description;
          metaDescription.setAttribute('content', `${truncatedDescription} Vote for this app idea on Zaptopia.`);
        }

        console.log('RequestDetailPage: Successfully loaded request:', foundRequest.title);
      } catch (error) {
        console.error('RequestDetailPage: Error fetching request:', error);
        setError(error.message || 'Failed to load request');
        
        Sentry.captureException(error, {
          extra: {
            requestId: id,
            userAuthenticated: !!user,
            userEmail: user?.email
          }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [id, !!user]);

  const handleVoteChange = (requestId, changes) => {
    console.log('RequestDetailPage: Vote changed for request:', requestId, changes);
    
    // Update the local request state
    if (request && request.id === requestId) {
      setRequest(prev => ({
        ...prev,
        ...changes
      }));
    }
  };

  const handleRequestUpdated = (updatedRequest) => {
    console.log('RequestDetailPage: Request updated:', updatedRequest);
    setRequest(updatedRequest);
  };

  const handleRequestDeleted = (requestId) => {
    console.log('RequestDetailPage: Request deleted:', requestId);
    // Navigate back to requests page after successful deletion
    navigate('/requests');
  };

  const handleSignInNeeded = () => {
    console.log('RequestDetailPage: User needs to sign in, opening auth modal');
    setShowAuthModal(true);
  };

  const handleCloseAuthModal = () => {
    console.log('RequestDetailPage: Closing auth modal');
    setShowAuthModal(false);
  };

  const handleSubmitIdea = () => {
    if (!user) {
      console.log('RequestDetailPage: User needs to sign in to submit idea');
      setShowAuthModal(true);
      return;
    }
    
    console.log('RequestDetailPage: Navigating to submit idea page');
    // Clear list state when navigating to submit page as user is leaving the list context
    listState.clear();
    navigate('/submit');
  };

  const handleShare = () => {
    if (navigator.share && request) {
      navigator.share({
        title: request.title,
        text: request.description,
        url: window.location.href,
      }).catch(err => {
        console.log('Error sharing:', err);
        // Fallback to copying to clipboard
        navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      });
    } else {
      // Fallback for browsers that don't support native sharing
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleBackToRequests = () => {
    console.log('RequestDetailPage: Navigating back to requests page');
    // Don't clear list state when going back to requests - we want to restore it
    navigate('/requests');
  };

  // Clean up state when leaving the detail page for destinations other than requests
  useEffect(() => {
    return () => {
      // This cleanup will run when the component unmounts
      // We use a small timeout to check if we're navigating back to requests
      setTimeout(() => {
        if (window.location.pathname !== '/requests') {
          console.log('RequestDetailPage: Cleaning up list state as user navigated away from app list context');
          listState.clear();
        }
      }, 100);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 relative overflow-hidden flex flex-col">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/15 to-purple-600/15 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/15 to-pink-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/10 to-violet-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
        </div>

        <Header onSignInNeeded={handleSignInNeeded} />
        <div className="relative z-10 flex-1 flex items-center justify-center py-20">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-t-transparent border-indigo-500 rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-20 h-20 border-4 border-t-transparent border-purple-400 rounded-full animate-spin opacity-60" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 relative overflow-hidden flex flex-col">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/15 to-purple-600/15 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/15 to-pink-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/10 to-violet-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
        </div>

        <Header onSignInNeeded={handleSignInNeeded} />
        <div className="relative z-10 flex-1 flex items-center justify-center py-20">
          <div className="text-center max-w-lg mx-auto p-6">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-red-500/30 via-orange-500/30 to-pink-500/30 rounded-3xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>
              <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/50">
                <svg className="w-16 h-16 text-red-500 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Request Not Found</h3>
                <p className="text-lg text-gray-600 mb-8">{error}</p>
                <button
                  onClick={() => navigate('/requests')}
                  className="group px-8 py-4 bg-gradient-to-r from-red-600 via-orange-600 to-pink-600 text-white font-bold text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-1"
                >
                  <div className="relative flex items-center justify-center">
                    <svg className="w-6 h-6 mr-3 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to App Requests
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 relative overflow-hidden flex flex-col">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/15 to-purple-600/15 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/15 to-pink-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/10 to-violet-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
        </div>

        <Header onSignInNeeded={handleSignInNeeded} />
        <div className="relative z-10 flex-1 flex items-center justify-center py-20">
          <div className="text-center">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/30 via-purple-500/30 to-pink-500/30 rounded-3xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>
              <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/50">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Request not found</h2>
                <button
                  onClick={() => navigate('/requests')}
                  className="group px-8 py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white font-bold text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-1"
                >
                  <div className="relative flex items-center justify-center">
                    <svg className="w-6 h-6 mr-3 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to App Requests
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 relative overflow-hidden flex flex-col">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/15 to-purple-600/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/15 to-pink-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/10 to-violet-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <Header onSignInNeeded={handleSignInNeeded} />
      
      <div className="relative z-10 flex-1">
        {/* Back Button Section */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <button
            onClick={handleBackToRequests}
            className="group inline-flex items-center text-indigo-600 hover:text-indigo-800 font-semibold p-3 hover:bg-white/60 rounded-2xl transition-all duration-300 backdrop-blur-sm border-2 border-transparent hover:border-indigo-200 transform hover:scale-105"
          >
            <svg className="w-5 h-5 mr-3 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-lg">Back to App Requests</span>
          </button>
        </div>
        
        <RequestDetail
          request={request}
          onVoteChange={handleVoteChange}
          onRequestUpdated={handleRequestUpdated}
          onRequestDeleted={handleRequestDeleted}
        />
      </div>
      
      <Footer />
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={handleCloseAuthModal} 
      />
    </div>
  );
}